import { useState } from 'react';
import { GraduationCap, Calendar, Pencil, Trash2, Plus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useRemoveEducation } from '../hooks';
import { EducationFormDialog } from './EducationFormDialog';
import type { CandidateEducation } from '../types';

interface EducationsSectionProps {
  candidateId: number;
  educations: CandidateEducation[];
}

export function EducationsSection({ candidateId, educations }: EducationsSectionProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CandidateEducation | null>(null);
  const removeEducation = useRemoveEducation();

  const handleAdd = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const handleEdit = (edu: CandidateEducation) => {
    setEditing(edu);
    setDialogOpen(true);
  };

  const handleDelete = async (edu: CandidateEducation) => {
    if (!confirm(`Supprimer la formation "${edu.degree}" à ${edu.institution} ?`)) return;
    await removeEducation.mutateAsync({ candidateId, educationId: edu.id });
  };

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between text-base">
            <div className="flex items-center gap-2">
              <GraduationCap className="h-4 w-4" />
              Formations
              {educations.length > 0 && (
                <span className="text-sm font-normal text-muted-foreground">
                  ({educations.length})
                </span>
              )}
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleAdd} title="Ajouter une formation">
              <Plus className="h-4 w-4" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {educations.length > 0 ? (
            <div className="space-y-3">
              {educations.map((edu, idx) => (
                <div key={edu.id}>
                  {idx > 0 && <Separator className="my-3" />}
                  <div className="group">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium text-sm">{edu.degree}</div>
                        {edu.field && <div className="text-sm text-muted-foreground">{edu.field}</div>}
                        <div className="text-sm text-muted-foreground">{edu.institution}</div>
                        {(edu.startDate || edu.endDate) && (
                          <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                            <Calendar className="h-3 w-3" />
                            {edu.startDate &&
                              new Date(edu.startDate).toLocaleDateString('fr-FR', { year: 'numeric' })}
                            {edu.startDate && edu.endDate && ' - '}
                            {edu.endDate &&
                              new Date(edu.endDate).toLocaleDateString('fr-FR', { year: 'numeric' })}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => handleEdit(edu)}
                          title="Modifier"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => handleDelete(edu)}
                          disabled={removeEducation.isPending}
                          title="Supprimer"
                        >
                          {removeEducation.isPending ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Button variant="ghost" size="sm" className="text-xs" onClick={handleAdd}>
              Ajouter une formation
            </Button>
          )}
        </CardContent>
      </Card>

      <EducationFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        candidateId={candidateId}
        education={editing}
      />
    </>
  );
}
